/**
 * Allow-list of HTTPS origins (browser-context popup/redirect mode) and
 * deep-link schemes (Capacitor mode) the auth-origin will accept as a
 * `return_origin` parameter. Anything else is rejected at parse time to
 * prevent the auth-origin being abused as an open-redirector.
 *
 * Kept in sync manually with deployed apps. When adding a new app:
 *   1. Add its origin / scheme here.
 *   2. Add the same value to its api_client row's allowed_origins JSON on
 *      the kernel side so /sso/exchange-ticket accepts it.
 */
const ALLOWED_RETURN_ORIGINS: ReadonlyArray<string> = [
  'https://monitoring.update-fitness.cloud',
  'https://mitarbeiter.update-fitness.ch',
  'https://calendar.update-fitness.cloud',
  'https://backups.update-fitness.cloud',
  'https://templates.update-fitness.cloud',
  'https://webform-builder.update-fitness.cloud',
  'https://door.update-fitness.cloud',
  'https://desktop.update-fitness.cloud',
  'updatecore-employee://sso/callback',
  'trainingsapp-v2://sso/callback',
  // Local-dev origins for plain-localhost passkey testing. WebAuthn
  // treats localhost as a secure context regardless of http/https, so
  // monitoring (5175) + employee (5181) Vite dev servers can launch
  // ceremonies against this auth-origin at 5186 without TLS.
  'http://localhost:5175',
  'http://localhost:5181',
];

export type PopupParams = {
  request_id: string;
  return_origin: string;
  api_client_id: string;
  username: string | undefined;
};

export type RedirectParams = {
  state: string;
  return_origin: string;
  api_client_id: string;
  username: string | undefined;
};

/**
 * Reads + validates URL params for the popup routes. Returns null when
 * any required field is missing or the return_origin is not allow-listed.
 */
export function read_popup_params(search: string): PopupParams | null {
  const params = new URLSearchParams(search);
  const request_id = params.get('request_id') ?? '';
  const return_origin = params.get('return_origin') ?? '';
  const api_client_id = params.get('api_client_id') ?? '';
  const username = params.get('username') ?? undefined;

  if (request_id === '' || return_origin === '' || api_client_id === '') return null;
  if (is_allowed_return_origin(return_origin) === false) return null;
  return { request_id, return_origin, api_client_id, username: username || undefined };
}

/**
 * Reads + validates URL params for the redirect routes. Same shape as the
 * popup params except `state` replaces `request_id` (the redirect flow is
 * stateless on the auth-origin side — the calling app stores the state in
 * sessionStorage and verifies on its /sso/callback page).
 */
export function read_redirect_params(search: string): RedirectParams | null {
  const params = new URLSearchParams(search);
  const state = params.get('state') ?? '';
  const return_origin = params.get('return_origin') ?? '';
  const api_client_id = params.get('api_client_id') ?? '';
  const username = params.get('username') ?? undefined;

  if (state === '' || return_origin === '' || api_client_id === '') return null;
  if (is_allowed_return_origin(return_origin) === false) return null;
  return { state, return_origin, api_client_id, username: username || undefined };
}

export function is_allowed_return_origin(value: string): boolean {
  return ALLOWED_RETURN_ORIGINS.indexOf(value) !== -1;
}
